import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { arcgisToGeoJSON } from '@terraformer/arcgis';
import * as turf from '@turf/turf';
import type { Geometry, MultiPolygon, Polygon } from 'geojson';
import { WGS84_SPATIAL_REFERENCE } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/spatialReference';
import { MAX_GEO_LOCATION_BYTES } from 'utils/validateGeoLocationSize';
import { buildGeoLocationFromGeoJson } from './buildGeoLocationFromGeoJson';

/** NOAA NWS FEMA Regions feature layer (Map Viewer item 3f9fecfdc4fc4c3cbd8f55c45fec0566). */
const FEMA_REGIONS_FEATURE_LAYER_URL =
  'https://services2.arcgis.com/C8EMgrsFcRFL6LrL/arcgis/rest/services/FEMA_Regions/FeatureServer/0';

const FEMA_REGION_ROMAN = [
  '',
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
] as const;

export type FemaRegionPolygon = {
  regionNumber: number;
  label: string;
  geometry: Polygon | MultiPolygon;
};

let cachedQuery: Promise<FemaRegionPolygon[]> | null = null;

function isRegionGeometry(
  geometry: Geometry
): geometry is Polygon | MultiPolygon {
  return geometry.type === 'Polygon' || geometry.type === 'MultiPolygon';
}

function regionNumberFromAttributes(
  attributes: Record<string, unknown> | nullish
): number | null {
  const raw = attributes?.REGION_NUM ?? attributes?.region_num;
  const parsed = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 10) return null;
  return parsed;
}

/** Bytes of a location payload, including a long address so reverse geocode still fits. */
export function locationPayloadBytes(geometry: Polygon | MultiPolygon): number {
  const location = buildGeoLocationFromGeoJson({
    geometry,
    centroid: {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [0, 0] },
      properties: {},
    },
    address: 'x'.repeat(300),
    placeName: 'FEMA Region VIII',
  });
  return new Blob([JSON.stringify(location)]).size;
}

function vertexCount(geometry: Polygon | MultiPolygon): number {
  let count = 0;
  const walk = (value: unknown) => {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === 'number') {
      count += 1;
      return;
    }
    value.forEach(walk);
  };
  walk(geometry.coordinates);
  return count;
}

export function fitGeometryToLocationLimit(
  geometry: Polygon | MultiPolygon
): Polygon | MultiPolygon {
  if (locationPayloadBytes(geometry) <= MAX_GEO_LOCATION_BYTES) {
    return geometry;
  }

  let current = geometry;
  let tolerance = 0.01;
  for (let attempt = 0; attempt < 12; attempt += 1) {
    let simplified: Polygon | MultiPolygon;
    try {
      const feature = turf.simplify(turf.feature(current), {
        tolerance,
        highQuality: false,
      });
      const next = feature.geometry;
      if (!next || (next.type !== 'Polygon' && next.type !== 'MultiPolygon')) {
        break;
      }
      simplified = next;
    } catch {
      break;
    }

    if (vertexCount(simplified) >= vertexCount(current)) {
      tolerance *= 2;
      if (tolerance > 1) break;
      continue;
    }

    current = simplified;
    if (locationPayloadBytes(current) <= MAX_GEO_LOCATION_BYTES) {
      return current;
    }
    tolerance *= 2;
    if (tolerance > 1) break;
  }

  return current;
}

async function queryFemaRegionPolygons(): Promise<FemaRegionPolygon[]> {
  const layer = new FeatureLayer({ url: FEMA_REGIONS_FEATURE_LAYER_URL });
  await layer.load();
  const { features } = await layer.queryFeatures({
    where: '1=1',
    returnGeometry: true,
    outFields: ['REGION', 'REGION_NUM'],
    outSpatialReference: WGS84_SPATIAL_REFERENCE,
  });

  const byNumber = new Map<number, Polygon | MultiPolygon>();
  for (const feature of features) {
    if (!feature.geometry) continue;
    const number = regionNumberFromAttributes(feature.attributes);
    if (number == null || byNumber.has(number)) continue;
    const geoJson = arcgisToGeoJSON(feature.geometry.toJSON()) as Geometry;
    if (!isRegionGeometry(geoJson)) continue;
    byNumber.set(number, fitGeometryToLocationLimit(geoJson));
  }

  const regions = [...byNumber.entries()]
    .sort(([left], [right]) => left - right)
    .map(([regionNumber, geometry]) => ({
      regionNumber,
      label: `FEMA Region ${FEMA_REGION_ROMAN[regionNumber]}`,
      geometry,
    }));

  if (regions.length === 0) {
    throw new Error('FEMA region query returned no polygons');
  }

  return regions;
}

export function loadFemaRegionPolygons(): Promise<FemaRegionPolygon[]> {
  if (!cachedQuery) {
    cachedQuery = queryFemaRegionPolygons().catch((error) => {
      cachedQuery = null;
      throw error;
    });
  }
  return cachedQuery;
}
